import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, X, Loader2, UserRound, Trophy, RefreshCw } from "lucide-react";
import { formatBRL } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { nome: "", telefone: "", comissao_pct: "", ativo: true };

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

export const VendedoresTab = ({ onUnauthorized }) => {
  const [resumo, setResumo] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sellers/comissoes`, { headers });
      setResumo(res.data);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setResumo([]); toast.error("Erro ao carregar equipe."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      nome: form.nome,
      telefone: form.telefone || "",
      comissao_pct: form.comissao_pct ? Number(form.comissao_pct) : 0,
      ativo: !!form.ativo,
    };
    try {
      if (form.id) {
        await axios.put(`${API}/sellers/${form.id}`, payload, { headers });
        toast.success("Vendedor atualizado.");
      } else {
        await axios.post(`${API}/sellers`, payload, { headers });
        toast.success("Vendedor cadastrado.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao salvar vendedor.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/sellers/${id}`, { headers });
      setResumo((rs) => rs.filter((r) => r.vendedor.id !== id));
      toast.success("Vendedor removido.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao remover vendedor.");
    }
  };

  const mesLabel = new Date().toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  return (
    <div data-testid="vendedores-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Equipe de vendas</h1>
          <p className="text-xs text-slate-400 mt-1">Comissões de {mesLabel}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="sellers-refresh" className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
            <RefreshCw className="w-4 h-4" /> Atualizar
          </button>
          <button
            onClick={() => setForm({ ...empty })} data-testid="seller-new-button"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
          >
            <Plus className="w-4 h-4" /> Novo vendedor
          </button>
        </div>
      </div>

      <AnimatePresence>
        {form && (
          <motion.form
            onSubmit={save}
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="glass rounded-3xl p-6 sm:p-8 mb-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="seller-form"
          >
            <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between">
              <h2 className="font-display font-bold text-white">{form.id ? "Editar vendedor" : "Novo vendedor"}</h2>
              <button type="button" onClick={() => setForm(null)} data-testid="seller-form-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <input className={inputCls} placeholder="Nome do vendedor*" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="seller-input-nome" />
            <input className={inputCls} placeholder="Telefone/WhatsApp" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="seller-input-telefone" />
            <div>
              <input className={inputCls} type="number" step="0.1" min="0" placeholder="Comissão (%)*" required value={form.comissao_pct} onChange={(e) => setForm({ ...form, comissao_pct: e.target.value })} data-testid="seller-input-comissao" />
              <p className="text-[11px] text-slate-500 mt-1.5 px-1">% sobre o valor da venda. Editável em cada fechamento.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 px-2">
              <input type="checkbox" checked={!!form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} data-testid="seller-input-ativo" className="accent-emerald-500 w-4 h-4" />
              Ativo (aparece no funil)
            </label>
            <div className="sm:col-span-2 lg:col-span-4 flex gap-3">
              <button type="submit" disabled={saving} data-testid="seller-save-button" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-3 rounded-full transition-colors">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {form.id ? "Salvar alterações" : "Cadastrar vendedor"}
              </button>
              <button type="button" onClick={() => setForm(null)} className="glass text-white text-sm font-semibold px-6 py-3 rounded-full">Cancelar</button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {resumo === null ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-[#131620] animate-pulse" />)}</div>
      ) : resumo.length === 0 ? (
        <p className="text-slate-400 text-sm glass rounded-2xl p-8" data-testid="sellers-empty">Nenhum vendedor cadastrado. Cadastre a equipe para atribuir negócios e calcular comissões.</p>
      ) : (
        <div className="space-y-3" data-testid="sellers-list">
          {resumo.map((r, idx) => (
            <div key={r.vendedor.id} className="glass rounded-2xl p-4" data-testid={`seller-card-${r.vendedor.id}`}>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 shrink-0">
                  {idx === 0 && r.comissao_mes > 0 ? <Trophy className="w-5 h-5" /> : <UserRound className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
                    {r.vendedor.nome}
                    {!r.vendedor.ativo && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-400">Inativo</span>}
                  </p>
                  <p className="text-sm text-slate-400 truncate">
                    Comissão padrão {r.vendedor.comissao_pct}%{r.vendedor.telefone ? ` · ${r.vendedor.telefone}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-5 text-right shrink-0">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Vendas no mês</p>
                    <p className="text-sm font-bold text-white" data-testid={`seller-vendas-mes-${r.vendedor.id}`}>{r.vendas_mes}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Vendido no mês</p>
                    <p className="text-sm font-bold text-white">{formatBRL(r.total_vendido_mes)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Comissão do mês</p>
                    <p className="text-sm font-bold text-emerald-400" data-testid={`seller-comissao-mes-${r.vendedor.id}`}>{formatBRL(r.comissao_mes)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setExpanded(expanded === r.vendedor.id ? null : r.vendedor.id)}
                    data-testid={`seller-detail-${r.vendedor.id}`}
                    className="h-10 px-3 rounded-full glass text-slate-300 text-xs font-semibold hover:text-emerald-400 transition-colors"
                  >
                    {expanded === r.vendedor.id ? "Ocultar" : "Vendas"}
                  </button>
                  <button
                    onClick={() => setForm({ ...r.vendedor, comissao_pct: r.vendedor.comissao_pct ?? "" })} data-testid={`seller-edit-${r.vendedor.id}`}
                    className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors" aria-label="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(r.vendedor.id)} data-testid={`seller-delete-${r.vendedor.id}`}
                    className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/25 transition-colors" aria-label="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <AnimatePresence>
                {expanded === r.vendedor.id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="mt-4 border-t border-slate-800/60 pt-4 space-y-2" data-testid={`seller-vendas-list-${r.vendedor.id}`}>
                      {r.vendas.length === 0 ? (
                        <p className="text-xs text-slate-500">Nenhuma venda deste vendedor no mês.</p>
                      ) : r.vendas.map((v) => (
                        <div key={v.id} className="flex items-center justify-between text-sm bg-[#0d0f17] rounded-xl px-4 py-2.5">
                          <span className="text-slate-300 truncate">{v.veiculo} {v.cliente ? `— ${v.cliente}` : ""}</span>
                          <span className="text-slate-500 text-xs shrink-0 mx-3">{v.data}</span>
                          <span className="text-white font-semibold shrink-0">{formatBRL(v.valor)}</span>
                          <span className="text-emerald-400 font-semibold shrink-0 ml-3">+{formatBRL(v.comissao)}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
